import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Key, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Reset Password page — Sprint 2 Batch 2 (May 2026).
// Step 2 of 2: user enters the 6-digit code + new password.
// Email is read from the ?email=... query param (set by /forgot-password).
export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    setEmail(searchParams.get("email") || "");
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirm) {
      setError("The two password fields don't match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        new_password: newPassword,
      });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else if (detail && typeof detail === "object") setError(detail.message || "Reset failed. Try again.");
      else setError("Network error. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/login" data-testid="back-to-login-link" className="inline-flex items-center gap-2 text-sm text-[#2563EB] hover:text-blue-700 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          {done ? (
            <div className="text-center space-y-3" data-testid="reset-success">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
                Password updated
              </h1>
              <p className="text-sm text-slate-500">Redirecting to sign in…</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Key className="w-5 h-5 text-[#2563EB]" />
                </div>
                <div>
                  <h1 className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Set a new password</h1>
                  <p className="text-xs text-slate-500 mt-0.5">Enter the 6-digit code we emailed you, then your new password.</p>
                </div>
              </div>
              <form onSubmit={submit} className="space-y-3" data-testid="reset-password-form">
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="reset-email-input"
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1.5 block">6-digit code</label>
                  <input
                    type="text"
                    required
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    data-testid="reset-code-input"
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-base tracking-[0.6em] font-mono text-center focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1.5 block">New password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    data-testid="reset-new-password-input"
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Confirm new password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-confirm-input"
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                  />
                </div>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg" data-testid="reset-error">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || code.length !== 6 || !newPassword || !confirm}
                  data-testid="reset-submit"
                  className="w-full bg-[#2563EB] hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</> : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
